import { router, usePage } from "@inertiajs/react"
import { ChevronLeft, Copy, Download, FileJson, Loader2, Quote, RefreshCw } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import {
  StudioQualityMarkToggleRow,
  StudioQualityPreviewDecor,
  studioQualityBorderClass,
  type StudioQualityValue,
} from "@/components/studio-quality-mark"
import type { StoryboardShotN } from "@/components/storyboard-shot-tile"
import { PlayBadge } from "@/components/media/asset-media-overlay"
import { useInitialLoadReporter } from "@/components/shot-panel/use-initial-load-reporter"
import { SeedanceDraftBadge, SeedanceDraftFinalizeButton } from "@/components/media/seedance-draft-finalize-controls"
import { postShotDraftFinalize } from "@/lib/inertia-actions"
import { parseDraftFinalize } from "@/lib/seedance-draft"
import { Button } from "@/components/ui/primitives"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { copyImageToClipboard, downloadImage, filenameFromUrl } from "@/lib/download-image"
import { useCsrfToken } from "@/hooks/use-csrf-token"
import { effectiveFrameFormat, frameFormatClasses } from "@/lib/frame-format"
import { cn } from "@/lib/utils"
import { MediaVersionPromptDialog } from "@/components/shot-panel/media-version-prompt-dialog"
import { VersionNameInlineEdit } from "@/components/shot-panel/version-name-inline-edit"
import { VideoVersionPreviewDialog } from "@/components/shot-panel/video-version-preview-dialog"
import { renameShotMediaVersion } from "@/components/shot-panel/timeline-editor/api"
import type { ShotMediaVersionPromptVersion } from "@/lib/workspace-api"
import type { GenerationJobRow, WorkspaceBaseProps } from "@/types/workspace"
import { activeShotJob, MEDIA_VERSION_JOB_KINDS, useReloadOnJobFinish } from "@/lib/shot-jobs"
import { jobStatusLabel, statusDetailLabel } from "@/components/jobs/job-elapsed-timer"
import { useShotPanelWorkspace } from "@/hooks/use-shot-panel-workspace"
import { useProgressClock } from "@/hooks/use-progress-clock"
import { estimatedJobProgress, isEstimatedGenerationPhase } from "@/lib/job-estimated-progress"
import { useTranslation } from "@/hooks/use-translation"
import { generationLogStatusLabel, mediaVersionKindTitle } from "@/lib/shot-i18n"
import { videoFirstFrameSrc } from "@/lib/video-timeline"

const EMPTY_WORKSPACE_JOBS: GenerationJobRow[] = []

export type ShotMediaVersionKind = "storyboard" | "video" | "shooting_board"

export type GenerationLogSummary = {
  id: number
  provider: string
  ai_model: string | null
  api_endpoint: string | null
  status: string
  duration_ms: number | null
  created_at: string
  request_payload: Record<string, unknown> | null
  error_message: string | null
  external_task_id: string | null
}

export type ShotMediaVersionTimelineMarkerRow = {
  id: number
  mark_ms: number
}

export type ShotMediaVersionDerivedFrom = {
  id: number
  created_at: string
  preview_thumb_url?: string | null
}

export type ShotMediaVersionRow = {
  id: number
  kind: string
  name?: string | null
  created_at: string
  preview_url: string | null
  download_url?: string | null
  stream_url?: string | null
  preview_thumb_url?: string | null
  first_frame_url?: string | null
  first_frame_thumb_url?: string | null
  last_frame_url?: string | null
  last_frame_thumb_url?: string | null
  active: boolean
  studio_quality?: StudioQualityValue
  generation_log: GenerationLogSummary | null
  duration_ms?: number | null
  frame_rate?: number | null
  timeline_frame_urls?: string[]
  timeline_frame_aspect_ratio?: number | null
  markers?: ShotMediaVersionTimelineMarkerRow[]
  derived_from_version_id?: number | null
  derived_from?: ShotMediaVersionDerivedFrom | null
  generation_seed?: number | null
  generation_model?: string | null
  generation_prompt?: string | null
  prompt_version?: ShotMediaVersionPromptVersion | null
  draft_finalize?: unknown
}

type Props = {
  projectId: number
  shotId: number
  kind: ShotMediaVersionKind
  /** Shot override; null/undefined inherits project. */
  shotFrameFormat?: string | null
  onClose: () => void
  onDataChanged: () => void
  onInitialLoadSettled?: () => void
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const PROVIDER_LABELS: Record<string, string> = {
  dashscope: "DashScope (Wan / Qwen)",
  kling: "Kling",
  n8n_gpt_image: "GPT Image (n8n)",
  openrouter: "OpenRouter",
  wan: "WAN (DashScope Video)",
}

function GenerationLogDetail({
  log,
  onBack,
  projectId,
  shotId,
  onRecovered,
}: {
  log: GenerationLogSummary
  onBack: () => void
  projectId: number
  shotId: number
  onRecovered?: () => void
}) {
  const { t } = useTranslation(["shot", "common"])
  const page = usePage<WorkspaceBaseProps>()
  const canViewDetails = page.props.project_permissions?.can_view_request_log_details === true
  const st = generationLogStatusLabel(log.status)
  const durationSec = canViewDetails && log.duration_ms != null ? (log.duration_ms / 1000).toFixed(1) : null
  const showCheckGeneration = canViewDetails && log.provider === "openrouter" && log.status === "failed"

  const [genIdInput, setGenIdInput] = useState(log.external_task_id ?? "")
  const [checking, setChecking] = useState(false)
  const [checkResult, setCheckResult] = useState<{ ok: boolean; message: string } | null>(null)
  const csrfToken = useCsrfToken()

  const handleCheckGeneration = async () => {
    const token = csrfToken
    if (!token) {
      setCheckResult({ ok: false, message: t("common.sessionExpired") })
      return
    }
    const gid = genIdInput.trim()
    if (!gid) {
      setCheckResult({ ok: false, message: t("mediaVersions.enterGenerationId") })
      return
    }
    setChecking(true)
    setCheckResult(null)
    try {
      const url = `/projects/${projectId}/workspace/shots/${shotId}/check_openrouter_generation`
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({ generation_id: gid, authenticity_token: token }),
      })
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string; error?: string }
      if (data.ok) {
        setCheckResult({ ok: true, message: data.message ?? t("mediaVersions.imageSaved") })
        onRecovered?.()
      } else {
        setCheckResult({ ok: false, message: data.error ?? t("mediaVersions.imageNotFound") })
      }
    } catch {
      setCheckResult({ ok: false, message: t("common.networkUnavailable") })
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 self-start text-xs text-ui-text-secondary hover:text-ui-text-primary"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        {t("mediaVersions.backToVersions")}
      </button>

      <div className="rounded-lg border border-ui-border bg-ui-surface-sunken p-3">
        <h4 className="mb-3 text-sm font-semibold text-ui-text-primary">{t("mediaVersions.apiRequest")}</h4>

        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
          <dt className="text-ui-text-muted">{t("mediaVersions.provider")}</dt>
          <dd className="text-ui-text-primary">{PROVIDER_LABELS[log.provider] ?? log.provider}</dd>

          {log.ai_model ? (
            <>
              <dt className="text-ui-text-muted">{t("mediaVersions.model")}</dt>
              <dd className="font-mono text-ui-text-primary">{log.ai_model}</dd>
            </>
          ) : null}

          {canViewDetails && log.api_endpoint ? (
            <>
              <dt className="text-ui-text-muted">Endpoint</dt>
              <dd className="font-mono text-ui-text-secondary">{log.api_endpoint}</dd>
            </>
          ) : null}

          <dt className="text-ui-text-muted">{t("mediaVersions.status")}</dt>
          <dd className={st.cls}>{st.label}</dd>

          {durationSec ? (
            <>
              <dt className="text-ui-text-muted">{t("mediaVersions.time")}</dt>
              <dd className="text-ui-text-primary tabular-nums">{durationSec}s</dd>
            </>
          ) : null}

          <dt className="text-ui-text-muted">{t("mediaVersions.date")}</dt>
          <dd className="text-ui-text-secondary tabular-nums">
            {new Date(log.created_at).toLocaleString("ru-RU", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </dd>

          {canViewDetails && log.external_task_id ? (
            <>
              <dt className="text-ui-text-muted">Generation ID</dt>
              <dd className="font-mono text-ui-text-secondary">{log.external_task_id}</dd>
            </>
          ) : null}

          {canViewDetails && log.error_message ? (
            <>
              <dt className="text-ui-text-muted">{t("mediaVersions.error")}</dt>
              <dd className="text-ui-status-failed">{log.error_message}</dd>
            </>
          ) : null}
        </dl>
      </div>

      {showCheckGeneration ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-950/20 p-3">
          <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-ui-status-review">
            <RefreshCw className="h-4 w-4" />
            {t("mediaVersions.checkGeneration")}
          </h4>
          <p className="mb-2.5 text-[11px] leading-relaxed text-ui-text-secondary">
            {t("mediaVersions.checkGenerationHint")}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={genIdInput}
              onChange={(e) => setGenIdInput(e.target.value)}
              placeholder={t("placeholders.generationId")}
              className="min-w-0 flex-1 rounded-lg border border-ui-border px-2.5 py-1.5 font-mono text-xs text-ui-text-primary placeholder:text-ui-text-secondary focus:border-ui-accent focus:ring-1 focus:ring-[var(--ui-focus-ring)]/30 focus:outline-none dark:bg-ui-bg-elevated light:bg-clay-1"
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={checking || !genIdInput.trim()}
              onClick={() => void handleCheckGeneration()}
              className="h-8 shrink-0 gap-1 text-xs"
            >
              {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {t("mediaVersions.check")}
            </Button>
          </div>
          {checkResult ? (
            <p className={cn("mt-2 text-xs", checkResult.ok ? "text-ui-status-approved" : "text-ui-status-failed")}>
              {checkResult.message}
            </p>
          ) : null}
        </div>
      ) : null}

      {canViewDetails && log.request_payload ? (
        <div className="rounded-lg border border-ui-border bg-ui-surface-sunken p-3">
          <h4 className="mb-2 text-sm font-semibold text-ui-text-primary">Request payload</h4>
          <pre className="it2tv-scrollbar max-h-[40vh] overflow-auto rounded-lg p-3 text-[11px] leading-relaxed break-all whitespace-pre-wrap text-ui-text-secondary dark:bg-ui-bg-elevated light:bg-clay-1">
            {JSON.stringify(log.request_payload, null, 2)}
          </pre>
        </div>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Version card with lazy video playback
// ---------------------------------------------------------------------------

function VersionCard({
  v,
  kind,
  ff,
  activatingId,
  qualitySavingId,
  projectId,
  shotId,
  onActivate,
  onSaveQuality,
  onViewLog,
  onDataChanged,
  onRename,
}: {
  v: ShotMediaVersionRow
  kind: ShotMediaVersionKind
  ff: { aspect: string }
  activatingId: number | null
  qualitySavingId: number | null
  projectId: number
  shotId: number
  onActivate: (id: number) => Promise<boolean>
  onSaveQuality: (id: number, next: StudioQualityValue) => void
  onViewLog: (log: GenerationLogSummary) => void
  onDataChanged: () => void
  onRename?: (id: number, name: string) => void
}) {
  const { t } = useTranslation(["shot", "common"])
  const [promptOpen, setPromptOpen] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null)
  const [copying, setCopying] = useState(false)
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isVideo = kind === "video"
  const poster = isVideo
    ? (v.first_frame_url ?? v.first_frame_thumb_url ?? v.preview_thumb_url ?? undefined)
    : undefined
  const thumbUrl = isVideo ? (poster ?? null) : v.preview_url
  const canPreviewVideo = isVideo && Boolean(v.preview_url)
  const canActivate = !v.active && activatingId === null
  const originalUrl = isVideo ? (v.download_url ?? null) : (v.download_url ?? v.preview_url)
  const downloadFilename = originalUrl
    ? filenameFromUrl(originalUrl, `${kind}_v${v.id}`, isVideo ? ".mp4" : undefined)
    : ""
  const createdLabel = new Date(v.created_at).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
  const showPromptButton = kind === "storyboard" || kind === "video"

  const handleDownloadOriginal = () => {
    if (originalUrl) void downloadImage(originalUrl, downloadFilename)
  }

  const handleCopyOriginal = async () => {
    if (!originalUrl || copying) return
    setCopying(true)
    try {
      await copyImageToClipboard(originalUrl)
      toast.success(t("common.originalCopied"))
    } catch {
      toast.error(t("common.copyImageFailed"))
    } finally {
      setCopying(false)
    }
  }

  // Single click → preview modal (video) or lightbox (image); double click → make current.
  const handleSingleClick = () => {
    if (canPreviewVideo) {
      setPreviewOpen(true)
    } else if (!isVideo && v.preview_url) {
      setLightboxOpen(true)
    }
  }

  const handleContainerClick = () => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current)
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null
      handleSingleClick()
    }, 250)
  }

  const handleActivateDoubleClick = () => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current)
      clickTimerRef.current = null
    }
    if (canActivate) void onActivate(v.id)
  }

  useEffect(
    () => () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current)
    },
    [],
  )

  return (
    <li
      className={cn(
        "flex flex-col rounded-lg border dark:bg-black/25 light:bg-clay-1",
        v.active ? "border-2 border-ui-accent" : "border-ui-border",
        !v.active && studioQualityBorderClass(v.studio_quality ?? null),
      )}
      onDoubleClick={handleActivateDoubleClick}
    >
      <div
        className={cn(
          "relative w-full cursor-pointer overflow-hidden rounded-t-lg dark:bg-ui-bg-elevated light:bg-clay-2",
          ff.aspect,
        )}
        title={canActivate ? t("mediaVersions.doubleClickActivate") : undefined}
        onClick={handleContainerClick}
      >
        {isVideo ? (
          poster || canPreviewVideo ? (
            <>
              {poster ? (
                <img src={poster} alt="" className="h-full w-full object-cover" />
              ) : (
                <video
                  src={videoFirstFrameSrc(v.preview_url)}
                  className="h-full w-full object-cover"
                  preload="metadata"
                  muted
                  playsInline
                />
              )}
              {canPreviewVideo ? (
                <div className="absolute inset-0 flex items-center justify-center bg-transparent transition-colors hover:bg-black/25">
                  <PlayBadge />
                </div>
              ) : null}
            </>
          ) : (
            <div className="flex h-full items-center justify-center px-2 text-center text-[10px] text-ui-text-secondary">
              {t("mediaVersions.noPreview")}
            </div>
          )
        ) : thumbUrl ? (
          <img
            src={thumbUrl}
            alt=""
            className="h-full w-full object-cover"
            onLoad={(e) => setNaturalSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          />
        ) : (
          <div className="flex h-full items-center justify-center px-2 text-center text-[10px] text-ui-text-secondary">
            {t("mediaVersions.noPreview")}
          </div>
        )}
        <StudioQualityPreviewDecor quality={v.studio_quality ?? null} />
        <div
          className="absolute top-1.5 right-1.5 z-[4]"
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <StudioQualityMarkToggleRow
            value={v.studio_quality ?? null}
            disabled={qualitySavingId !== null || activatingId !== null}
            dense
            onSet={(next) => void onSaveQuality(v.id, next)}
          />
        </div>
        {v.active && (
          <span className="absolute top-1.5 left-1.5 z-[3] rounded bg-ui-accent/90 px-1.5 py-0.5 text-[9px] font-semibold tracking-wide text-ui-on-accent uppercase">
            {t("mediaVersions.current")}
          </span>
        )}
        {parseDraftFinalize(v.draft_finalize) ? (
          <span className="pointer-events-none absolute bottom-1.5 left-1.5 z-[3]">
            <SeedanceDraftBadge />
          </span>
        ) : null}
        {showPromptButton ? (
          <button
            type="button"
            className={cn(
              "absolute bottom-1.5 z-[3] rounded bg-[var(--ui-chip-bg-medium)] p-1 text-ui-text-secondary ring-1 ring-ui-border backdrop-blur-sm",
              parseDraftFinalize(v.draft_finalize) ? "left-10" : "left-1.5",
              v.prompt_version ? "hover:bg-ui-accent/90 hover:text-ui-on-accent" : "cursor-not-allowed opacity-40",
            )}
            title={v.prompt_version ? t("mediaVersions.versionPrompt") : t("mediaVersions.promptNotSaved")}
            disabled={!v.prompt_version}
            onClick={(e) => {
              e.stopPropagation()
              if (v.prompt_version) setPromptOpen(true)
            }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <Quote className="h-3.5 w-3.5" />
          </button>
        ) : null}
        {v.generation_log && (
          <button
            type="button"
            className="absolute right-1.5 bottom-1.5 z-[3] rounded bg-[var(--ui-chip-bg-medium)] p-1 text-ui-text-secondary ring-1 ring-ui-border backdrop-blur-sm hover:bg-ui-accent/90 hover:text-ui-on-accent"
            title={t("mediaVersions.apiRequestTitle")}
            onClick={(e) => {
              e.stopPropagation()
              onViewLog(v.generation_log!)
            }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <FileJson className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="flex flex-col gap-0.5 p-2">
        {kind === "video" && onRename && v.name != null ? (
          <div className="-mx-1 flex min-w-0">
            <VersionNameInlineEdit
              name={v.name}
              label={v.name}
              title={t("timeline.renameVersion")}
              onCommit={(next) => onRename(v.id, next)}
              className="max-w-full flex-1"
            />
          </div>
        ) : null}
        <p className="text-[10px] text-ui-text-muted tabular-nums">{createdLabel}</p>
        {parseDraftFinalize(v.draft_finalize) ? (
          <div className="mt-1" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
            <SeedanceDraftFinalizeButton
              finalize={parseDraftFinalize(v.draft_finalize)!}
              projectId={projectId}
              onConfirm={async (resolution) => {
                await postShotDraftFinalize(projectId, shotId, v.id, resolution)
                onDataChanged()
              }}
            />
          </div>
        ) : null}
        {kind === "video" && v.generation_model ? (
          <p className="truncate font-mono text-[10px] text-ui-text-secondary" title={v.generation_model}>
            {v.generation_model}
          </p>
        ) : null}
        {originalUrl ? (
          <div className="mt-1 flex items-center gap-1.5">
            <button
              type="button"
              className="inline-flex min-w-0 items-center gap-1 rounded-lg border border-ui-border bg-ui-surface-raised px-2 py-1 text-[10px] text-ui-text-primary transition-colors hover:bg-ui-surface-raised"
              title={t("common.downloadOriginal")}
              onClick={(e) => {
                e.stopPropagation()
                handleDownloadOriginal()
              }}
            >
              <Download className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{t("common.downloadOriginal")}</span>
              {naturalSize ? (
                <span className="shrink-0 text-ui-text-secondary tabular-nums">
                  {naturalSize.w}&times;{naturalSize.h}
                </span>
              ) : null}
            </button>
            {!isVideo ? (
              <button
                type="button"
                disabled={copying}
                className="inline-flex shrink-0 items-center justify-center rounded-lg border border-ui-border bg-ui-surface-raised p-1 text-ui-text-primary transition-colors hover:bg-ui-surface-raised disabled:opacity-40"
                title={t("mediaVersions.copyOriginalToClipboard")}
                onClick={(e) => {
                  e.stopPropagation()
                  void handleCopyOriginal()
                }}
              >
                {copying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {!isVideo && v.preview_url ? (
        <SketchDialog
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
          projectId={projectId}
          sourceUrl={v.download_url ?? v.preview_url}
          presentation="view"
          allowEdit={false}
          persist="none"
          nested
          headerTitle={createdLabel}
        />
      ) : null}
      {isVideo && v.preview_url ? (
        <VideoVersionPreviewDialog
          version={v}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          activating={activatingId === v.id}
          onActivate={onActivate}
          qualitySaving={qualitySavingId !== null}
          onSaveQuality={onSaveQuality}
        />
      ) : null}
      {v.prompt_version ? (
        <MediaVersionPromptDialog
          projectId={projectId}
          shotId={shotId}
          promptVersion={v.prompt_version}
          open={promptOpen}
          onOpenChange={setPromptOpen}
          onDataChanged={onDataChanged}
        />
      ) : null}
    </li>
  )
}

// ---------------------------------------------------------------------------
// Placeholder card shown while a generation job is running
// ---------------------------------------------------------------------------

function PendingVersionCard({ job, ff }: { job: GenerationJobRow; ff: { aspect: string } }) {
  const detail = job.status_detail ? statusDetailLabel(job.status_detail) : jobStatusLabel(job.status)
  const createdAt = job.started_at ?? job.created_at
  const now = useProgressClock(isEstimatedGenerationPhase(job))
  const progress = estimatedJobProgress(job, now)

  return (
    <li className="flex flex-col rounded-lg border border-ui-border dark:bg-black/25 light:bg-clay-1">
      <div
        className={cn(
          "relative flex w-full items-center justify-center overflow-hidden rounded-t-lg dark:bg-ui-bg-elevated light:bg-clay-2",
          ff.aspect,
        )}
      >
        <Loader2 className="h-5 w-5 animate-spin text-ui-text-muted" />
      </div>
      <div className="flex flex-col gap-1.5 p-2">
        <div className="flex items-center justify-between gap-1">
          <p className="text-[10px] text-ui-text-muted tabular-nums">
            {createdAt
              ? new Date(createdAt).toLocaleString("ru-RU", {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""}
          </p>
          <span className="text-[10px] font-semibold text-ui-status-rendering">
            {detail}
            {progress > 0 ? <span className="ml-1 tabular-nums">{progress}%</span> : null}
          </span>
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-ui-surface-raised">
          <div className="h-full rounded-full bg-ui-accent transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>
    </li>
  )
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export function ShotMediaVersionsView({
  projectId,
  shotId,
  kind,
  shotFrameFormat,
  onClose,
  onDataChanged,
  onInitialLoadSettled,
}: Props) {
  const { t } = useTranslation(["shot", "common", "jobs"])
  const page = usePage<Partial<WorkspaceBaseProps>>()
  const { project } = useShotPanelWorkspace()
  const jobs = page.props.jobs ?? EMPTY_WORKSPACE_JOBS
  const ff = frameFormatClasses(effectiveFrameFormat(shotFrameFormat, project?.frame_format ?? "9:16"))

  const [versions, setVersions] = useState<ShotMediaVersionRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activatingId, setActivatingId] = useState<number | null>(null)
  const [qualitySavingId, setQualitySavingId] = useState<number | null>(null)
  const [viewingLog, setViewingLog] = useState<GenerationLogSummary | null>(null)
  const csrfToken = useCsrfToken()
  const reportInitialLoad = useInitialLoadReporter(onInitialLoadSettled)

  const jobKinds: readonly string[] = MEDIA_VERSION_JOB_KINDS[kind]
  const pendingJobs = useMemo(
    () =>
      jobs
        .filter(
          (job) =>
            (job.status === "queued" || job.status === "processing") &&
            jobKinds.includes(job.job_kind) &&
            (job.shot_ids ?? []).includes(shotId),
        )
        .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? "")),
    [jobs, jobKinds, shotId],
  )

  const load = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      const silent = opts.silent === true
      if (!silent) {
        setLoading(true)
        setError(null)
      }
      try {
        const url = `/projects/${projectId}/workspace/shots/${shotId}/media_versions?kind=${encodeURIComponent(kind)}`
        const res = await fetch(url, {
          headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          credentials: "same-origin",
        })
        if (!res.ok) {
          if (!silent) {
            setError(t("mediaVersions.loadFailed"))
            setVersions([])
          }
          return
        }
        const data = (await res.json()) as { versions?: ShotMediaVersionRow[] }
        setVersions(Array.isArray(data.versions) ? data.versions : [])
      } catch {
        if (!silent) {
          setError(t("common.networkUnavailable"))
          setVersions([])
        }
      } finally {
        if (!silent) {
          setLoading(false)
          reportInitialLoad()
        }
      }
    },
    [projectId, shotId, kind, t, reportInitialLoad],
  )

  useEffect(() => {
    setVersions([])
    setError(null)
    setViewingLog(null)
    setActivatingId(null)
    setQualitySavingId(null)
    void load()
  }, [load])

  const activeJob = useMemo(() => activeShotJob(jobs, shotId, jobKinds), [jobs, shotId, jobKinds])

  const handleJobFinished = useCallback(() => {
    void load({ silent: true })
    onDataChanged()
  }, [load, onDataChanged])

  useReloadOnJobFinish(activeJob, handleJobFinished)

  const activate = async (versionId: number): Promise<boolean> => {
    const token = csrfToken
    if (!token) {
      setError(t("common.sessionExpiredRetry"))
      return false
    }
    setActivatingId(versionId)
    setError(null)
    try {
      const url = `/projects/${projectId}/workspace/shots/${shotId}/media_versions/${versionId}/activate`
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({ authenticity_token: token }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean
        shot?: StoryboardShotN
        error?: string
      }
      if (!res.ok || data.ok === false) {
        setError(data.error ?? t("mediaVersions.activateFailed"))
        return false
      }
      setVersions((list) => list.map((x) => ({ ...x, active: x.id === versionId })))
      onDataChanged()
      router.reload({ only: ["tree"] })
      return true
    } catch {
      setError(t("common.networkUnavailable"))
      return false
    } finally {
      setActivatingId(null)
    }
  }

  const saveStudioQuality = async (versionId: number, next: StudioQualityValue) => {
    const token = csrfToken
    if (!token) {
      setError(t("common.sessionExpired"))
      return
    }
    const snapshot = versions.map((x) => ({ ...x }))
    setQualitySavingId(versionId)
    setError(null)
    setVersions((list) => list.map((x) => (x.id === versionId ? { ...x, studio_quality: next } : x)))
    try {
      const url = `/projects/${projectId}/workspace/shots/${shotId}/media_versions/${versionId}/studio_quality`
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({ studio_quality: next, authenticity_token: token }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean
        version?: ShotMediaVersionRow
        error?: string
      }
      if (!res.ok || !data.ok || !data.version) {
        setVersions(snapshot)
        setError(data.error ?? t("mediaVersions.qualitySaveFailed"))
        return
      }
      setVersions((list) => list.map((x) => (x.id === versionId ? { ...x, ...data.version! } : x)))
    } catch {
      setVersions(snapshot)
      setError(t("common.networkUnavailable"))
    } finally {
      setQualitySavingId(null)
    }
  }

  const handleRename = useCallback(
    (versionId: number, name: string) => {
      setVersions((list) => list.map((x) => (x.id === versionId ? { ...x, name } : x)))
      void renameShotMediaVersion(projectId, shotId, versionId, name)
        .then((updated) => {
          setVersions((list) => list.map((x) => (x.id === updated.id ? { ...x, name: updated.name } : x)))
          onDataChanged()
        })
        .catch((err: unknown) => {
          void load({ silent: true })
          toast.error(err instanceof Error ? err.message : t("timeline.renameFailed"))
        })
    },
    [projectId, shotId, load, onDataChanged, t],
  )

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 px-4 pb-2">
        <h2 className="text-sm font-semibold text-ui-text-primary">{mediaVersionKindTitle(kind)}</h2>
        <p className="mt-0.5 text-xs text-ui-text-muted">{t("mediaVersions.doubleClickHint")}</p>
      </div>
      <div className="it2tv-scrollbar min-h-0 flex-1 overflow-y-auto pt-3">
        {viewingLog ? (
          <GenerationLogDetail
            log={viewingLog}
            onBack={() => setViewingLog(null)}
            projectId={projectId}
            shotId={shotId}
            onRecovered={() => {
              router.reload({ only: ["tree"] })
              onDataChanged()
              onClose()
            }}
          />
        ) : loading ? (
          <p className="py-8 text-center text-sm text-ui-text-muted">{t("common.loading")}</p>
        ) : error ? (
          <p className="py-4 text-center text-sm text-ui-status-rendering/95">{error}</p>
        ) : versions.length === 0 && pendingJobs.length === 0 ? (
          <p className="py-8 text-center text-sm text-ui-text-muted">{t("mediaVersions.empty")}</p>
        ) : (
          <ul className="grid grid-cols-2 gap-3 p-1 sm:grid-cols-3">
            {pendingJobs.map((job) => (
              <PendingVersionCard key={`job-${job.id}`} job={job} ff={ff} />
            ))}
            {versions.map((v) => (
              <VersionCard
                key={v.id}
                v={v}
                kind={kind}
                ff={ff}
                activatingId={activatingId}
                qualitySavingId={qualitySavingId}
                projectId={projectId}
                shotId={shotId}
                onActivate={activate}
                onSaveQuality={saveStudioQuality}
                onViewLog={setViewingLog}
                onDataChanged={onDataChanged}
                onRename={kind === "video" ? handleRename : undefined}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
